import { AxiosError, type AxiosResponse } from 'axios'
import { useMutation } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { getEndpointWithParams } from 'queries/queries-utils'
import { isPreFlightErrorResponse } from 'queries/status-checks/pre-flight/utils'
import { preFlightMutationKeys } from 'queries/status-checks/query-keys'

import type {
  DynatracePreFlightErrorResponse,
  DynatracePreFlightParams,
  DynatracePreFlightResponse,
  DynatracePreFlightSuccessResponse,
  UseDynatracePreFlightOptions
} from 'queries/status-checks/pre-flight/types'

function postDynatracePreFlight(
  mutationKey: ReturnType<typeof preFlightMutationKeys.dynatrace>
) {
  return async function ({
    signal,
    ...payload
  }: DynatracePreFlightParams): Promise<DynatracePreFlightSuccessResponse> {
    const endpointWithParams = getEndpointWithParams(...mutationKey)
    const response = await agent.post<DynatracePreFlightResponse>(
      endpointWithParams,
      payload,
      { signal }
    )

    /*
     * The control plane may encounter an issue while attempting to reach the Dynatrace API
     * to validate the provided configuration during the pre-flight check. In this case, the
     * response code will still return as a successful HTTP status, so we manually construct
     * and throw an AxiosError to be caught and handled upstream.
     */
    if (isPreFlightErrorResponse(response.data)) {
      throw new AxiosError<DynatracePreFlightErrorResponse>(
        response.data.errorMessage,
        '400',
        response.config,
        response.request,
        response as AxiosResponse<DynatracePreFlightErrorResponse>
      )
    }

    return response.data
  }
}

export function useDynatracePreFlightCheck(
  options?: UseDynatracePreFlightOptions
) {
  const { currentTeamId: teamId } = useAuth()
  const mutationKey = preFlightMutationKeys.dynatrace(teamId)

  return useMutation({
    ...options,
    mutationKey,
    mutationFn: postDynatracePreFlight(mutationKey)
  })
}
